import { analyticsModule } from '@/store/analytics/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { mockExamModule } from '@/store/mockExam/module'
import { quizModule } from '@/store/quiz/module'
import { subscriptionModule } from '@/store/subscription/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

const getGuidsForExperiment = () => {
    return [ 'PPEF01', 'PPEF02', 'PPPS02', 'PPSW04', 'PPSW03' ]
}

const getReadinessScoreRF = () => {
    const currentUEM = userExamMetadataModule.getters.getCurrentUserExamMetadata()

    const readinessScoreRF = currentUEM?.readiness?.find(r => r.model === 'rf_model')
    return readinessScoreRF?.confidence || null
}

const getReadinessScoreXGB = () => {
    const currentUEM = userExamMetadataModule.getters.getCurrentUserExamMetadata()

    const readinessScoreXGB = currentUEM?.readiness?.find(r => r.model === 'xgb_model')
    return readinessScoreXGB?.confidence || null
}

const getReadinessReportLevel = () => {
    const readinessScoreRF = getReadinessScoreRF()
    if (typeof readinessScoreRF !== 'number') {
        return 'Unknown'
    } else if (readinessScoreRF < 40) {
        return 'Low'
    } else if (readinessScoreRF < 60) {
        return 'Moderate'
    } else if (readinessScoreRF < 80) {
        return 'Good'
    } else if (readinessScoreRF >= 80) {
        return 'High'
    }
}

const getPercentQuestionBankComplete = () => {
    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const answers = quizModule.getters.getLatestAnswers({ questionFilter: 'all' })
    const answerCount = Object.keys(answers).length
    const totalQuestionCount = (currentExamMetadata?.itemCount || 0) - (currentExamMetadata?.archivedCount || 0)
    const percentQuestionBankComplete = Math.round(((answerCount / (totalQuestionCount || Infinity)) || 0) * 100)
    return percentQuestionBankComplete
}

const getOverallStudyScore = () => {
    const answers = Object.values(quizModule.getters.getLatestAnswers({ questionFilter: 'all' }))
    const correctAnswers = answers.filter(a => a.isCorrect)

    const studyScore = Math.round((correctAnswers.length / (answers.length || Infinity)) * 100)
    return studyScore
}

const getWeakestSubject = () => {
    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const currentExamMetadataSubjects = examMetadataModule.state.subjectsWithLevels
    const uemDisabledSubjects = userExamMetadataModule.getters.getUemDisabledSubjects(
        currentExamMetadata?.examGuid,
        currentExamMetadataSubjects
    )
    const sortedSubjectStats = quizModule.getters.getSortedSubjectStats()
    const userDisabledSubjects = uemDisabledSubjects

    const weakestSubject = sortedSubjectStats.find(subjectInfo => !userDisabledSubjects.includes(subjectInfo.id))
    return weakestSubject || null
}

const getCanSeeReadinessReport = () => {
    const currentUser = userModule.state.user
    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const daysUntilExam = userExamMetadataModule.getters.getDaysUntilExam()
    const percentQuestionBankComplete = getPercentQuestionBankComplete()
    const currentExamGuid = currentExamMetadata?.examGuid
    const guidsForExperiment = getGuidsForExperiment()

    if (!currentUser || !currentExamMetadata || !currentExamGuid) {
        return false
    }

    // Don't show if not one of these exams: NREMT EMT, NREMT Paramedic, NBCC NCE, ASWB MSW and ASWB LCSW
    if (!guidsForExperiment.includes(currentExamGuid)) {
        return false
    }

    // Don't show report to unsubscribed users, even if studying a free exam
    const subscription = subscriptionModule.getters.getSubscriptionForExamId()
    // subscription is `true` for free exams, object for subscribers and licensed users, and `false` otherwise
    if (typeof subscription === 'boolean') {
        return false
    }

    // Don't show report to users studying an exam with no mock exams available
    const areMockExamsAvailable = mockExamModule.getters.getCurrentMockExams().length > 0
    if (!areMockExamsAvailable) {
        return false
    }

    // Don't show report to users without a readiness score
    const readinessScoreRF = getReadinessScoreRF()
    if (!readinessScoreRF) {
        return false
    }

    // don't show report to users with exam date that's outside of within next 35 days wndow
    if (typeof daysUntilExam !== 'number' || daysUntilExam < 0 || daysUntilExam > 35) {
        return false
    }

    // Don't show report to users with less than 25% question bank completion
    if (percentQuestionBankComplete < 25) {
        return false
    }

    return true
}

const getReadinessReport = () => {
    const readinessScoreRF = getReadinessScoreRF()
    const readinessScoreXGB = getReadinessScoreXGB()
    const readinessReportLevel = getReadinessReportLevel()
    const readinessReportScore = ((readinessScoreRF || 0) / 10).toFixed(1)
    const daysUntilExam = userExamMetadataModule.getters.getDaysUntilExam()
    const percentQuestionBankComplete = getPercentQuestionBankComplete()
    const overallStudyScore = getOverallStudyScore()

    return {
        readinessScoreRF,
        readinessScoreXGB,
        readinessReportLevel,
        readinessReportScore,
        daysUntilExam,
        percentQuestionBankComplete,
        overallStudyScore,
    }
}

const getAmplitudeReadinessProps = (route: RouteLocationNormalizedLoaded) => {
    const screenName = analyticsModule.getters.getScreenName(route)
    const readinessReport = getReadinessReport()

    return {
        readinessReportLevel: readinessReport.readinessReportLevel,
        readinessReportScore: readinessReport.readinessReportScore,
        readinessScoreRF: readinessReport.readinessScoreRF || undefined,
        readinessScoreXGB: readinessReport.readinessScoreXGB || undefined,
        screenName,
        daysUntilExam: readinessReport.daysUntilExam || undefined,
        averageScore: readinessReport.overallStudyScore,
        percentQuestionBankComplete: readinessReport.percentQuestionBankComplete,
        overallPercentCorrect: getOverallPercentCorrect(),
        averageStudyDaysPerWeek: getAverageStudyDaysPerWeek(),
        averageQuestionsPerWeek: getAverageQuestionsPerWeek(),
        latestMockExamScore: getLatestMockExamScore(),
    }
}

const getOverallPercentCorrect = () => {
    const answers = quizModule.getters.getQuizzes().flatMap(q => q.answers)
    const serialObjs = answers.reduce(
        (accum, answer) => {
            accum.answered.add(answer.questionSerial)
            if (answer.isCorrect) {
                accum.correct.add(answer.questionSerial)
            }
            return accum
        },
        { correct: new Set() as Set<string>, answered: new Set() as Set<string> })

    const percentQuestionsCorrect = serialObjs.answered.size
        ? Math.round(serialObjs.correct.size * 100 / serialObjs.answered.size)
        : 0

    return percentQuestionsCorrect
}

const getRecentQuizPeriod = () => {
    const quizzes = quizModule.getters.getQuizzes().filter(q => q.mode !== 5)
    if (!quizzes.length) {
        return null
    }

    const now = new Date()
    const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30)

    const firstQuizDate = quizzes.reduce((min, q) => {
        const d = new Date(q.createdAt)
        return d < min ? d : min
    }, new Date(quizzes[0].createdAt))

    const periodStart = firstQuizDate > thirtyDaysAgo ? firstQuizDate : thirtyDaysAgo
    const periodDays = (now.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)
    const weeks = Math.max(periodDays / 7, 1)
    const recentQuizzes = quizzes.filter(q => new Date(q.createdAt) >= periodStart)

    return { recentQuizzes, weeks }
}

const getAverageStudyDaysPerWeek = () => {
    const period = getRecentQuizPeriod()
    if (!period) {
        return 0
    }

    const toLocalDayKey = (d: Date) =>
        `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`

    const uniqueStudyDays = new Set(period.recentQuizzes.map(q => toLocalDayKey(new Date(q.createdAt))))

    return Math.round(uniqueStudyDays.size / period.weeks)
}

const getAverageQuestionsPerWeek = () => {
    const period = getRecentQuizPeriod()
    if (!period) {
        return 0
    }

    const totalAnswers = period.recentQuizzes.reduce((sum, q) => sum + q.answers.length, 0)

    return Math.round(totalAnswers / period.weeks)
}

const getLatestMockExamScore = () => {
    const quizzes = quizModule.getters.getQuizzes()
        .filter(q => q.mode === 5)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    const lastMockExam = quizzes[0]
    if (!lastMockExam) {
        return -1
    }
    const correctCount = lastMockExam.correctCount
    const incorrectCount = lastMockExam.incorrectCount

    return Math.round((correctCount / (correctCount + incorrectCount)) * 100)
}

const getPasserAverages = () => {
    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const currentExamGuid = currentExamMetadata?.examGuid
    const guidsWithPasserData = getGuidsForExperiment()

    if (!currentExamGuid || !guidsWithPasserData.includes(currentExamGuid)) {
        return null
    }

    if (currentExamGuid === 'PPEF01') { // NREMT EMT
        return {
            overallPercentCorrect: 87,
            questionBankCompletionPercent: 60,
            latestMockExamScore: 67,
            avgStudyDaysPerWeek: 3,
            avgQuestionsPerWeek: 153,
        }
    } else if (currentExamGuid === 'PPEF02') { // NREMT Paramedic
        return {
            overallPercentCorrect: 84,
            questionBankCompletionPercent: 61,
            latestMockExamScore: 61,
            avgStudyDaysPerWeek: 3,
            avgQuestionsPerWeek: 103,
        }
    } else if (currentExamGuid === 'PPPS02') { // NBCC NCE
        return {
            overallPercentCorrect: 82,
            questionBankCompletionPercent: 68,
            latestMockExamScore: 69,
            avgStudyDaysPerWeek: 4,
            avgQuestionsPerWeek: 122,
        }
    } else if (currentExamGuid === 'PPSW04') { // ASWB MSW
        return {
            overallPercentCorrect: 89,
            questionBankCompletionPercent: 65,
            latestMockExamScore: 64,
            avgStudyDaysPerWeek: 4,
            avgQuestionsPerWeek: 155,
        }
    } else if (currentExamGuid === 'PPSW03') { // ASWB LCSW
        return {
            overallPercentCorrect: 89,
            questionBankCompletionPercent: 63,
            latestMockExamScore: 60,
            avgStudyDaysPerWeek: 4,
            avgQuestionsPerWeek: 106,
        }
    }
}

export default {
    getReadinessScoreRF,
    getReadinessScoreXGB,
    getReadinessReportLevel,
    getPercentQuestionBankComplete,
    getOverallStudyScore,
    getWeakestSubject,
    getCanSeeReadinessReport,
    getReadinessReport,
    getAmplitudeReadinessProps,
    getOverallPercentCorrect,
    getLatestMockExamScore,
    getAverageStudyDaysPerWeek,
    getAverageQuestionsPerWeek,
    getPasserAverages,
}
